import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import {
  FaCrosshairs,
  FaCommentDots,
  FaFilm,
  FaRegQuestionCircle,
  FaRegCopy,
  FaCheck,
  FaArrowRight,
} from "react-icons/fa";
import { ReactComponent as BasicIcon } from "../../MVVM/Assets/Avatar/Icons/essentials.svg";
import { ReactComponent as StandardIcon } from "../../MVVM/Assets/Avatar/Icons/pro.svg";
import { ReactComponent as UnlimitedIcon } from "../../MVVM/Assets/Avatar/Icons/platinum.svg";

const BIND = 'bind "f3" "say !allstar"';

const FAQ = [
  {
    q: "Does it really not use my clip limit?",
    a: "Right. !allstar clips don't draw from your normal clip allowance. If you're tapped out for the month, you can still keep making them in game.",
  },
  {
    q: "Do I need the Allstar app running?",
    a: "No. The command lives in match chat and the clip is built later from the match demo, so nothing has to be open while you play.",
  },
  {
    q: "What if I type it a little late?",
    a: "You're covered. Sent during the next round's buy time or roughly its first 20 seconds, it looks back and clips the round you just finished.",
  },
  {
    q: "Does team chat work?",
    a: "No, say it in all-chat. Team chat isn't recorded into the match demo, so a team-only !allstar won't be picked up.",
  },
  {
    q: "How do I change the bind key?",
    a: 'Swap "f3" for any key you like, then paste the line into the CS2 console or your autoexec.',
  },
  {
    q: "Why isn't my clip showing up?",
    a: "First, refresh your match history so we can pick up the latest game. If it still isn't there, upload the demo and we'll clip it for you.",
  },
];

const AllstarPage = () => {
  const [copied, setCopied] = useState(false);
  const [processing, setProcessing] = useState(false);
  const videoRef = useRef(null);

  // Video ends on the !allstar being typed: show a "processing the clip"
  // beat for ~2.6s, then loop back to the start.
  const handleVideoEnded = () => {
    setProcessing(true);
    setTimeout(() => {
      setProcessing(false);
      videoRef.current?.play();
    }, 3800);
  };

  const copyBind = async () => {
    try {
      await navigator.clipboard.writeText(BIND);
    } catch (e) {
      /* clipboard unavailable */
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <Page>
      <Header>
        <H1>
          Skip clip limits with <Cmd>!allstar</Cmd>.
        </H1>
        <Lede>
          Just type <Cmd>!allstar</Cmd> in all chat after you do something you
          want to clip. If we can access your match, we&apos;ll make the clip{" "}
          <Strong>for free, even if you&apos;ve hit your clip limit.</Strong>
        </Lede>
      </Header>

      <DemoWrap>
        <MediaFrame>
          <Media>
            <video
              ref={videoRef}
              src={`${process.env.REACT_APP_S3_CLIP_BUCKET}/static/web/in-game/example_allstar.mp4`}
              autoPlay
              muted
              playsInline
              onEnded={handleVideoEnded}
            />
            {processing && (
              <Processing>
                <Spinner aria-hidden="true" />
                <ProcessingText>
                  <small>
                    <Cmd>!allstar</Cmd> detected
                  </small>
                  Processing <span>Deagle 3K on Dust 2</span>
                </ProcessingText>
                <ProcessingBar />
              </Processing>
            )}
          </Media>
        </MediaFrame>
        <MediaCap>
          Someone typing <Cmd>!allstar</Cmd> after a sick 3K!
        </MediaCap>
      </DemoWrap>

      <Timeline>
        <Beat>
          <span className="ico">
            <FaCrosshairs />
          </span>
          <h3>Make a play</h3>
          <p>
            Pop off in Premier, Competitive, or Wingman. Works on FACEIT and
            other third-party services too, with nothing open or recording.
          </p>
        </Beat>
        <Arrow aria-hidden="true" />
        <Beat>
          <span className="ico">
            <FaCommentDots />
          </span>
          <h3>
            Type <Cmd>!allstar</Cmd>
          </h3>
          <p>
            Send it in all-chat <strong>after you&apos;ve made a play</strong>{" "}
            or at the end of a round.
          </p>
        </Beat>
        <Arrow aria-hidden="true" />
        <Beat>
          <span className="ico">
            <FaFilm />
          </span>
          <h3>Captured automatically</h3>
          <p>
            Once your match is processed, we auto-clip every round you called{" "}
            <Cmd>!allstar</Cmd>, and none of them count against your clip limit.
          </p>
        </Beat>
      </Timeline>

      <BindBlock>
        <BindLabel>Bind it once. Then one tap clips your round.</BindLabel>
        <Snippet>
          <code>
            bind &quot;f3&quot; &quot;say <span>!allstar</span>&quot;
          </code>
          <CopyBtn
            type="button"
            copied={copied}
            onClick={copyBind}
            aria-label={copied ? "Copied" : "Copy bind command"}
          >
            {copied ? <FaCheck /> : <FaRegCopy />}
          </CopyBtn>
        </Snippet>
        <BindFootnote>
          <BindHelp tabIndex={0}>
            <FaRegQuestionCircle aria-hidden="true" />
            How to add this in CS2
            <BindTooltip role="tooltip">
              <ol>
                <li>
                  Open the CS2 developer console (the <Cmd>~</Cmd> key). If
                  nothing happens, turn it on in Game Settings &gt; Game &gt;
                  Enable Developer Console.
                </li>
                <li>
                  Want a different key? Swap <Cmd>f3</Cmd> for whatever you
                  like.
                </li>
                <li>Paste the command above into the console and hit Enter.</li>
                <li>
                  In a match, tap that key after a play to fire off{" "}
                  <Cmd>!allstar</Cmd>.
                </li>
              </ol>
            </BindTooltip>
          </BindHelp>
          <Dot aria-hidden="true">·</Dot>
          No bind? Just type <Cmd>!allstar</Cmd> in chat, it works the same.
        </BindFootnote>
      </BindBlock>

      <ValueBand>
        <h2>Always free. Never counts against your clip limit.</h2>
        <p className="sub">
          Every <Cmd>!allstar</Cmd> clip is free to make and never touches your
          monthly allowance.
        </p>
        <Plans>
          <Plan>
            <span className="tier">
              <BasicIcon aria-hidden="true" />
              Free &amp; Basic
            </span>
            <ul>
              <li>Never counts against your clip limit</li>
              <li>Always free to use</li>
              <li>Allstar-branded loadout</li>
            </ul>
          </Plan>
          <Plan as={Link} to="/upgrade" className="paid">
            <span className="tier">
              <StandardIcon aria-hidden="true" />
              <UnlimitedIcon aria-hidden="true" />
              With Standard or Unlimited
            </span>
            <ul>
              <li>Your own custom loadout and branding</li>
              <li>Ability to remove Watermark</li>
              <li>Priority queues for your clips</li>
            </ul>
            <span className="cta">
              Upgrade <FaArrowRight aria-hidden="true" />
            </span>
          </Plan>
        </Plans>
      </ValueBand>

      <Section>
        <SecTitle>Questions &amp; Answers</SecTitle>
        <QAGrid>
          {FAQ.map((item) => (
            <QA key={item.q}>
              <h3>{item.q}</h3>
              <p>{item.a}</p>
            </QA>
          ))}
        </QAGrid>
      </Section>
    </Page>
  );
};

const Page = styled.div`
  width: 100%;
  max-width: 1080px;
  margin: 0 auto;
  padding: 56px 22px 120px;
  color: ${(p) => p.theme.colors.ice};
`;

const Cmd = styled.span`
  font-family: "SFMono-Regular", Menlo, Consolas, monospace;
  color: ${(p) => p.theme.colors.envy};
  font-weight: 600;
  white-space: nowrap;
`;

const Header = styled.header`
  max-width: 60ch;
  margin: 0 auto;
  text-align: center;
`;

const H1 = styled.h1`
  font-size: 58px;
  line-height: 1.04;
  font-weight: 800;
  letter-spacing: -0.025em;
  margin: 0;

  @media (max-width: ${(p) => p.theme.breakpoints[1]}) {
    font-size: 40px;
  }
`;

const Lede = styled.p`
  color: ${(p) => p.theme.colors.chalk};
  font-size: 19px;
  line-height: 1.55;
  margin: 16px auto 0;

  @media (max-width: ${(p) => p.theme.breakpoints[1]}) {
    font-size: 17px;
  }
`;

const Strong = styled.span`
  color: ${(p) => p.theme.colors.ice};
  font-weight: 600;
`;

const DemoWrap = styled.div`
  max-width: 760px;
  margin: 40px auto 0;
`;

const MediaFrame = styled.div`
  position: relative;
  border-radius: 16px;
  box-shadow: 0 26px 70px -34px rgba(0, 0, 0, 0.85);
`;

const Media = styled.div`
  position: relative;
  aspect-ratio: 16 / 9;
  border-radius: 16px;
  overflow: hidden;
  background: linear-gradient(
    160deg,
    #1b2c33 0%,
    ${(p) => p.theme.colors.navy900} 75%
  );

  video {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const fillBar = keyframes`
  from { width: 0; }
  to { width: 100%; }
`;

const Processing = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 24px;
  text-align: center;
  background: rgba(11, 17, 22, 0.82);
  backdrop-filter: blur(3px);
  animation: ${fadeIn} 0.2s ease;
`;

const Spinner = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 3px solid rgba(255, 255, 255, 0.15);
  border-top-color: ${(p) => p.theme.colors.envy};
  animation: ${spin} 0.8s linear infinite;
`;

const ProcessingText = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: ${(p) => p.theme.colors.ice};

  small {
    display: block;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.04em;
    color: ${(p) => p.theme.colors.chalk};
    margin-bottom: 7px;
  }
  span {
    color: ${(p) => p.theme.colors.envy};
  }
`;

const ProcessingBar = styled.div`
  width: 70%;
  max-width: 240px;
  height: 5px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  overflow: hidden;

  &::after {
    content: "";
    display: block;
    height: 100%;
    border-radius: 999px;
    background: ${(p) => p.theme.colors.envy};
    animation: ${fillBar} 3.6s ease forwards;
  }
`;

const MediaCap = styled.p`
  text-align: center;
  font-size: 13.5px;
  color: ${(p) => p.theme.colors.chalk};
  opacity: 0.8;
  margin: 10px 0 0;
`;

/* Inline three-beat flow tied to the demo above (no numbered cards). */
const Timeline = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 8px;
  max-width: 860px;
  margin: 40px auto 0;

  @media (max-width: ${(p) => p.theme.breakpoints[2]}) {
    flex-direction: column;
    align-items: stretch;
    gap: 18px;
  }
`;

const Beat = styled.div`
  flex: 1;
  min-width: 0;
  padding: 0 6px;

  .ico {
    display: inline-grid;
    place-items: center;
    color: ${(p) => p.theme.colors.envy};
    margin-bottom: 12px;
  }
  .ico svg {
    width: 26px;
    height: 26px;
  }
  h3 {
    font-size: 19px;
    font-weight: 700;
    margin: 0;
  }
  p {
    color: ${(p) => p.theme.colors.chalk};
    font-size: 15px;
    line-height: 1.6;
    margin: 8px 0 0;
  }
  p strong {
    color: ${(p) => p.theme.colors.ice};
    font-weight: 600;
  }

  @media (max-width: ${(p) => p.theme.breakpoints[2]}) {
    display: grid;
    grid-template-columns: auto 1fr;
    column-gap: 16px;
    padding: 0;

    .ico {
      margin: 0;
      grid-row: span 2;
    }
  }
`;

/* Chevron between beats; hidden once the timeline stacks. */
const Arrow = styled.span`
  flex: none;
  align-self: center;
  width: 16px;
  height: 16px;
  margin-top: 5px;
  border-top: 2px solid rgba(255, 255, 255, 0.22);
  border-right: 2px solid rgba(255, 255, 255, 0.22);
  transform: rotate(45deg);

  @media (max-width: ${(p) => p.theme.breakpoints[2]}) {
    display: none;
  }
`;

const BindBlock = styled.div`
  max-width: 520px;
  margin: 46px auto 0;
  text-align: center;
`;

const BindLabel = styled.p`
  font-size: 15.5px;
  color: ${(p) => p.theme.colors.chalk};
  margin: 0 0 14px;
`;

/* Console-style code snippet with a corner copy button (not an input + side
   button), to read as a dev command rather than a form field. */
const Snippet = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  text-align: left;
  background: ${(p) => p.theme.colors.navy900};
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 15px 52px 15px 18px;

  code {
    flex: 1;
    min-width: 0;
    overflow-x: auto;
    font-family: "SFMono-Regular", Menlo, Consolas, monospace;
    font-size: 15.5px;
    color: ${(p) => p.theme.colors.ice};
    white-space: nowrap;
  }
  code span {
    color: ${(p) => p.theme.colors.envy};
  }
`;

const CopyBtn = styled.button`
  position: absolute;
  top: 50%;
  right: 9px;
  transform: translateY(-50%);
  display: inline-grid;
  place-items: center;
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  background: ${(p) =>
    p.copied ? "transparent" : "rgba(255, 255, 255, 0.08)"};
  color: ${(p) => (p.copied ? p.theme.colors.envy : p.theme.colors.ice)};
  transition: background 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.16);
  }
  &:focus-visible {
    outline: 2px solid ${(p) => p.theme.colors.envy};
    outline-offset: 2px;
  }
  svg {
    width: 15px;
    height: 15px;
  }
`;

const BindFootnote = styled.div`
  font-size: 14px;
  color: ${(p) => p.theme.colors.chalk};
  margin: 14px 0 0;
`;

const Dot = styled.span`
  margin: 0 8px;
  opacity: 0.5;
`;

const BindHelp = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  color: ${(p) => p.theme.colors.envy};
  cursor: help;

  svg {
    flex: none;
  }

  &:hover > [role="tooltip"],
  &:focus-within > [role="tooltip"] {
    opacity: 1;
    visibility: visible;
    transform: translate(-50%, 0);
  }
`;

const BindTooltip = styled.div`
  position: absolute;
  top: calc(100% + 10px);
  left: 50%;
  z-index: 5;
  width: 320px;
  max-width: 86vw;
  padding: 14px 16px 14px 6px;
  background: ${(p) => p.theme.colors.ned};
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  box-shadow: 0 24px 60px -24px rgba(0, 0, 0, 0.9);
  opacity: 0;
  visibility: hidden;
  transform: translate(-50%, -4px);
  transition:
    opacity 0.15s ease,
    transform 0.15s ease,
    visibility 0.15s;
  text-align: left;

  ol {
    margin: 0;
    padding-left: 24px;
    display: flex;
    flex-direction: column;
    gap: 9px;
  }
  li {
    font-size: 14.5px;
    line-height: 1.5;
    font-weight: 400;
    color: ${(p) => p.theme.colors.ice};
  }
  &::after {
    content: "";
    position: absolute;
    top: -5px;
    left: 50%;
    margin-left: -4px;
    width: 9px;
    height: 9px;
    background: ${(p) => p.theme.colors.ned};
    border-left: 1px solid rgba(255, 255, 255, 0.12);
    border-top: 1px solid rgba(255, 255, 255, 0.12);
    transform: rotate(45deg);
  }
`;

/* "Always free" message plus two additive benefit cards (free / premium),
   instead of a Leetify-style head-to-head Free-vs-Premium comparison table. */
const ValueBand = styled.section`
  margin: 72px auto 0;
  max-width: 820px;
  text-align: center;

  h2 {
    font-size: 28px;
    font-weight: 800;
    letter-spacing: -0.02em;
    margin: 0;
  }
  .sub {
    color: ${(p) => p.theme.colors.chalk};
    font-size: 16px;
    line-height: 1.55;
    max-width: 54ch;
    margin: 12px auto 0;
  }

  @media (max-width: ${(p) => p.theme.breakpoints[1]}) {
    h2 {
      font-size: 23px;
    }
  }
`;

const Plans = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
  margin-top: 30px;
  text-align: left;

  @media (max-width: ${(p) => p.theme.breakpoints[1]}) {
    grid-template-columns: 1fr;
  }
`;

const Plan = styled.div`
  display: block;
  background: ${(p) => p.theme.colors.navy500};
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
  padding: 24px 24px 26px;

  &.paid {
    color: inherit;
    text-decoration: none;
    border-color: ${(p) => p.theme.colors.envy};
    background: ${(p) => p.theme.colors.envyAlpha10};
    transition:
      transform 0.15s ease,
      box-shadow 0.15s ease;
  }
  &.paid:hover {
    transform: translateY(-2px);
    box-shadow: 0 18px 50px -28px rgba(0, 0, 0, 0.9);
  }
  &.paid:focus-visible {
    outline: 2px solid ${(p) => p.theme.colors.envy};
    outline-offset: 2px;
  }

  .tier {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: ${(p) => p.theme.colors.chalk};
    margin-bottom: 16px;
  }
  .tier svg {
    height: 22px;
    width: auto;
  }
  &.paid .tier {
    color: ${(p) => p.theme.colors.envy};
  }
  ul {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 11px;
  }
  li {
    position: relative;
    padding-left: 22px;
    font-size: 15px;
    line-height: 1.45;
    color: ${(p) => p.theme.colors.ice};
  }
  li::before {
    content: "";
    position: absolute;
    left: 0;
    top: 7px;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${(p) => p.theme.colors.envy};
  }
  .cta {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    margin-top: 18px;
    font-size: 14px;
    font-weight: 700;
    color: ${(p) => p.theme.colors.envy};
  }
  .cta svg {
    width: 13px;
    height: 13px;
    transition: transform 0.15s ease;
  }
  &.paid:hover .cta svg {
    transform: translateX(3px);
  }
`;

const Section = styled.section`
  padding: 64px 0 0;
`;

const SecTitle = styled.h2`
  font-size: 28px;
  font-weight: 800;
  letter-spacing: -0.02em;
  margin: 0 0 26px;
  text-align: center;
`;

/* Plain always-visible Q&A grid (no accordion / +- toggle). */
const QAGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 28px 40px;

  @media (max-width: ${(p) => p.theme.breakpoints[1]}) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

const QA = styled.div`
  h3 {
    font-size: 17px;
    font-weight: 700;
    color: ${(p) => p.theme.colors.ice};
    margin: 0;
  }
  p {
    color: ${(p) => p.theme.colors.chalk};
    font-size: 15px;
    line-height: 1.6;
    margin: 8px 0 0;
  }
`;

export default AllstarPage;
